import { createRouter, createWebHistory } from 'vue-router';
import CarrierDashboard from '@/views/carrier/Dashboard.vue';
// import BrokerDashboard from '@/views/broker/Dashboard.vue'
import Login from '@/views/login/Login.vue';
import LoginWithEmail from '@/views/login/LoginWithEmail.vue';
import LoginVerification from '@/views/login/Verification.vue';
import LoginWithHighway from '@/views/LoginWithHighway.vue';
import AccessPending from '@/views/common/AccessPending.vue';
import OAuthAuthorizations from '@/views/common/oauth/Authorizations.vue';
import PrivateModeBlock from '@/views/common/PrivateModeBlock.vue';
import PrivacyPolicy from '@/views/PrivacyPolicy.vue';
import Logout from '@/views/common/Logout.vue';
import store from '../store';
import dispatcher from './dispatcher';
import carrier from './carrier';
import signup from './signup';
import SSODemo from '@/views/SSODemo.vue';
import LoadBoardSSO from '@/views/LoadBoardSSO.vue';

const routes = [
  // Do not remove this.  It handles when a user gets redirected
  // after logging in before they are attached to a carrier
  {
    path: '/',
    name: 'Home',
    component: CarrierDashboard,
    meta: { pageTitle: 'Home' },
  },
  // Session paths
  {
    path: '/login',
    name: 'Login',
    component: Login,
    redirect: { name: 'CarrierDetails' },
    meta: { pageTitle: 'Login', doesNotRequireAuthentication: true },
  },
  {
    path: '/login-with-email',
    name: 'LoginWithEmail',
    component: LoginWithEmail,
    meta: { pageTitle: 'Login', doesNotRequireAuthentication: true },
  },
  {
    path: '/oauth/authorizations',
    name: 'OAuth Authorizations',
    component: OAuthAuthorizations,
    meta: { pageTitle: 'OAuth Authorization' },
  },
  // Login Flow
  {
    path: '/login/code',
    name: 'LoginVerification',
    component: LoginVerification,
    meta: { pageTitle: 'Login Code', doesNotRequireAuthentication: true },
  },
  {
    path: '/login-with-highway',
    name: 'LoginWithHighway',
    component: LoginWithHighway,
    meta: { pageTitle: 'Login', doesNotRequireAuthentication: true },
  },
  {
    path: '/single-sign-on-demo',
    name: 'SSODemo',
    component: SSODemo,
    meta: { pageTitle: 'SSO Demo', doesNotRequireAuthentication: true },
  },
  {
    path: '/single-sign-on-demo/loadboard',
    name: 'LoadBoard SSO Demo',
    component: LoadBoardSSO,
    meta: {
      pageTitle: 'LoadBoard SSO Demo',
      doesNotRequireAuthentication: true,
    },
  },
  {
    path: '/access-pending',
    name: 'AccessPending',
    component: AccessPending,
    meta: {
      pageTitle: 'Access Pending',
      doesNotRequireAuthentication: true,
      doesNotRequireCompany: true,
    },
  },
  {
    path: '/private-mode-block',
    name: 'PrivateModeBlock',
    component: PrivateModeBlock,
    meta: {
      pageTitle: 'Private Browsing Detected',
      doesNotRequireAuthentication: true,
      doesNotRequireCompany: true,
    },
  },
  {
    path: '/logout',
    name: 'Logout',
    component: Logout,
    meta: { pageTitle: 'Logout' },
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: {
      pageTitle: 'Privacy Policy',
      doesNotRequireAuthentication: false,
      doesNotRequireCompany: true,
    },
  },
  ...signup,
  ...carrier,
  ...dispatcher,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    meta: { pageTitle: 'Page Not Found' },
    component: () => import('../views/NotFound.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  let title = 'Highway - ' + (to.meta.pageTitle || to.name);

  if (to.name === 'Load Detail' || to.name === 'Load Board Detail') {
    title = `Load: ${to.params.id}`;
  }

  document.title = title;
  await store.dispatch('users/refreshCurrentUser');
  let currentUser = store.state.users.currentUser;
  let currentCompany = currentUser?.company;
  let companyType = currentCompany?.company_type;
  let homePage;

  await Promise.all([
    store.dispatch('users/refreshCurrentFeatureFlags', currentUser?.id),
    store.dispatch('users/getUserInquiries', currentUser?.id),
  ]);
  const enableRateConfirmation =
    store.state.users.featureFlags?.enable_rate_confirmation_page?.enabled;

  const enableCarrierLoads =
    store.state.users.featureFlags?.enable_carrier_loads?.enabled;

  const enableCarrierLoadBoard =
    store.state.users.featureFlags?.enable_carrier_load_board?.enabled;

  let isIDVOnboardingActive = false;
  let isIDVOnboardedActive = false;

  const status = store.state.users.currentUser?.identity_proofing_status;
  const isIdentityVerified =
    store.state.users.currentUser?.last_verified_identity_at;

  if (isIdentityVerified == null) {
    if (status === 'skip_identity_proofing') {
      // Skip IDV
      isIDVOnboardingActive = false;
      isIDVOnboardedActive = false;
    } else if (status === 'require_identity_proofing') {
      // Force IDV
      isIDVOnboardingActive = true;
      isIDVOnboardedActive = true;
    } else {
      // Fallback to the feature flags if the status is neither 1 nor 2
      isIDVOnboardingActive = !!store.getters['users/getFeatureFlag']?.(
        'idv_onboarding_flow'
      )?.['enabled'];
      isIDVOnboardedActive =
        !!store.getters['users/getFeatureFlag']?.('idv_onboarded_flow')?.[
          'enabled'
        ];
    }
  }

  let isOnboardingEmailVerificationActive = store.getters[
    'users/getFeatureFlag'
  ]?.('onboarding_email_verification')?.['enabled'];

  let isPrivacyPolicyStepEnabled = store.getters['users/getFeatureFlag']?.(
    'privacy_policy_step_enabled'
  )?.['enabled'];

  let isCarrierProductEnabled =
    store.getters['users/getFeatureFlag']?.('carrier_product')?.['enabled'];

  let latestInquiryStatus = store.getters['users/inquiryStatus'];

  // If a message was passed from the server in the query param message,
  // then store in localStorage so that it can be showed when the page
  // finishes loading
  if (to.query && to.query.message?.length > 0) {
    // Not storing the serverToastMessage when the user hits Verify Equipment button in Rules Assessment Status, because we need to
    // display the toast in the next render, not in the next refresh.
    if (
      !(
        from.name === 'Carrier Rules Assessment Status' &&
        to.name === 'Carrier Dashboard'
      )
    ) {
      localStorage.setItem('serverToastMessage', to.query.message);
    }
  }

  if (to.fullPath?.includes('sso_demo')) {
    console.log('SSO Demo Redirect');
    const url = new URL(window.location.origin + to.fullPath);

    url.searchParams.delete('sso_demo');

    localStorage.setItem(
      'redirect_sso_demo',
      url.pathname + '?' + url.searchParams.toString()
    );

    next({ name: 'SSODemo' });
  }

  if (!enableRateConfirmation) {
    const restrictedRoutes = ['Rate Confirmations', 'Rate Confirmation'];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'Carrier Brokers' });
    }
  }

  if (!enableCarrierLoads) {
    const restrictedRoutes = ['My Loads', 'Load Detail'];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'Carrier Brokers' });
    }
  }

  if (!enableCarrierLoadBoard) {
    const restrictedRoutes = [
      'Load Board',
      'Load Board Detail',
      'Exchange',
      'Book Now',
    ];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'Carrier Brokers' });
    }
  }

  if (!isCarrierProductEnabled) {
    // List the route names where this rule applies
    const restrictedRoutes = [
      'Carrier Brokers Search Results',
      'Carrier Brokers Show Page',
    ];

    if (restrictedRoutes.includes(to.name)) {
      return next({ name: 'Carrier Brokers' });
    }
  }

  // Need to move this up sooner in the process
  // so that the carrier doesn't get redirected away before we can
  // save them
  await storeOauthParams(to);

  switch (companyType) {
    case 'carrier':
      homePage = { name: 'Carrier Dashboard' };
      break;
    // case 'broker':
    // homePage = {name: 'Broker Dashboard'}
    // window.open('/onboarding/broker');
    // break
    case 'dispatcher':
      homePage = { name: 'Dispatcher Dashboard' };
      break;

    // Allow brokers to login and claim profiles
    case 'broker':
      homePage = { name: 'Carrier Dashboard' };
      // await storeOauthParams(to);
      return await handleBrokerUser(
        currentUser,
        currentCompany,
        to,
        from,
        next
      );
    // window.location.pathname = '/broker';
    // window.location.href = `${process.env.API_URL}/broker`;
    // window.location = `${process.env.API_URL}/broker`;
    // break;
  }

  if (
    process.env.NODE_ENV === 'development' &&
    to.fullPath === '/login-with-highway'
  ) {
    return next();
  }

  const managerUserExists = (element) => element === 'manage_users';
  const managerUserScope = currentUser?.scopes.some(managerUserExists);
  if (!managerUserScope && to.path.includes('/users')) {
    // Handle user company requests where the user is not
    // assigned a jwt
    if (!currentUser && to.path.includes('/carrier/users')) {
      localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
      sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
      return next({ name: 'SignUp' });
    } else {
      //If a user doesn't have manage_users as a scope. Redirect to the home
      window.location.pathname = '/';
    }
  }

  // Account is locked -- redirect to holding tank view.  Only
  // becomes unlocked when they have a company associated with them
  if (
    currentUser?.company_verification_status.includes('locked') &&
    !currentUser?.company?.id
  ) {
    console.log('user verification status locked; redirecting');
    if (to.name == 'AccessPending') {
      return next();
    } else {
      return next({ name: 'AccessPending' });
    }
  }
  var isEmailRequiredForLogin =
    store.state.carriers?.isEmailRequiredForLogin || false;

  // User has already submitted a carrier request and verified phone,
  // but has not entered first_name/email
  if (
    currentUser?.requested_carrier_id &&
    !currentUser?.first_name &&
    to.meta.doesNotRequireAuthentication &&
    !isEmailRequiredForLogin &&
    !isIDVOnboardingActive
  ) {
    console.log(
      'has submitted carrier request but not verified phone; redirecting'
    );
    return next({ name: 'CompanyType' });
  }

  // Pages that do not require the user to be logged in
  if (to.meta.doesNotRequireAuthentication) {
    console.log('page does not require authentication');
    // if they are logged in, take them to their home page
    //if (currentUser?.id > 0) { return next(homePage)}
    // if they are not logged in, let them get to the page

    return next();
  }

  if (latestInquiryStatus === 'declined' && isIDVOnboardingActive) {
    next({ name: 'VerificationFail' });
  }

  // Try and get the user from state and if not in state,
  // authenticate with the server to get the authenticated user
  if (currentUser?.id == null) {
    console.log('user is not logged in; save to redirect_url', to);

    // Only save if going to a path that is the dashboard or OAuth
    if (
      to.fullPath?.includes('oauth') ||
      to.fullPath?.includes('carrier') ||
      to.fullPath?.includes('dispatcher') ||
      to.fullPath?.includes('broker')
    ) {
      localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
      sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    }
    // If the initial path was oauth, then take them
    // to sign up vs. login.  This may need to change after a certain
    // amount of market adoption and when most carriers have a login
    if (to.fullPath?.includes('oauth')) {
      next({ name: 'SignUp' });
    } else {
      // return next({ name: "Login" });
      return next({ name: 'SignUp' });
    }
  }

  // The page does not require that we validate
  // that a user is associated with a company
  if (to.meta.doesNotRequireCompany) {
    console.log('view does not require company');
    return next();
  }

  /**
   * Determines if the user should proceed to the next step in the onboarding flow.
   *
   * This function checks multiple conditions related to the current session, company data,
   * onboarding status, user information, and verification statuses to decide if the
   * identity verification onboarding should redirect the user. It prevents progression if
   * email verification is required and the user's email is not yet verified.
   *
   * If all conditions are met, the function retrieves the redirect path after IDV approval
   * and navigates to it.
   *
   * @returns {void} - Redirects the user if conditions are met, or exits if any condition fails.
   */
  const isNewSession = !from.name;
  if (
    !currentCompany?.id &&
    isNewSession &&
    isIDVOnboardingActive &&
    currentUser.email &&
    currentUser.preferred_name &&
    !(isOnboardingEmailVerificationActive && !currentUser?.is_verified_email)
  ) {
    const redirectAfterIDV =
      store.getters['identityVerification/getRedirectAfterIDV'];
    console.log('New Session', redirectAfterIDV, latestInquiryStatus);

    if (
      latestInquiryStatus === 'approved' &&
      (redirectAfterIDV.name || redirectAfterIDV.path)
    ) {
      next(
        redirectAfterIDV.name
          ? { name: redirectAfterIDV.name }
          : redirectAfterIDV.path
      );
    }
  }

  if (
    isPrivacyPolicyStepEnabled &&
    !currentUser?.privacy_policy_consented_at &&
    localStorage.getItem('resBackup')
  ) {
    console.log('user has not consented to privacy policy; redirecting');
    return next({
      path: '/sign-up/verification',
      query: { privacy_policy: 'true' },
    });
  }

  if (
    isIDVOnboardingActive &&
    !companyType &&
    latestInquiryStatus !== 'approved' &&
    latestInquiryStatus !== 'declined'
  ) {
    console.log('Redirect user to IDV Sign up');
    return next({ name: 'IdentityVerification' });
  }

  if (
    isIDVOnboardingActive &&
    !companyType &&
    latestInquiryStatus == 'approved' &&
    currentUser?.email &&
    !currentUser?.is_verified_email &&
    isOnboardingEmailVerificationActive
  ) {
    console.log('Redirect user to Email Verification');
    return next({ name: 'EmailVerificationPending' });
  }

  // Does not have a company
  if (!currentCompany?.id) {
    localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
    sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    console.log('user does not have a company; redirecting');
    // Should we take them here or to General Information
    return next({ name: 'CompanyType' });
  }

  // The page does not require we validate that
  // a user has completed their metadata information
  if (to.meta.doesNotRequireCompletedUser) {
    console.log('view does not required meta data to be completed');
    return next();
  }

  if (isIDVOnboardingActive && !companyType && !currentUser?.preferred_name) {
    console.log('Redirect to user to Preferred Name');
    return next({ name: 'PreferredName' });
  }
  // Does not have an email address
  if (currentUser.email == null) {
    localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
    sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    console.log('user email is blank; redirecting');
    if (isIDVOnboardingActive) {
      return next({ path: '/sign-up/company-email' });
    }
    return next({ name: 'Carrier Claim My Profile' });
  }

  // Does not have a phone
  if (currentUser.phone_e164 == null && !isEmailRequiredForLogin) {
    localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
    sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    console.log('user phone is blank; redirecting');
    return next({ name: 'Carrier Claim My Profile' });
  }

  // Does not have a name
  if (currentUser.first_name == null) {
    localStorage.setItem('redirect_url_after_sigin', JSON.stringify(to));
    sessionStorage.setItem('redirect_url_after_signin', JSON.stringify(to));
    console.log('user first_name is blank; redirecting');
    return next({ name: 'Carrier Claim My Profile' });
  }

  // So at this point, the user is logged in, belongs to a company, has completed
  // all their user profile information, so lets make sure that if they are
  // company type broker they go to a broker endpoint, carrier to a carrier
  // and dispatcher to dispatcher

  if (isIDVOnboardedActive) {
    if (latestInquiryStatus === 'declined') {
      next({ name: 'VerificationFail' });
      return;
    }

    if (
      latestInquiryStatus !== 'approved' &&
      latestInquiryStatus !== 'declined'
    ) {
      console.log('IDV-Modal: Enable IDV Modal');
      store.commit('identityVerification/setShowIDVFlow', true);
    } else if (!currentUser?.preferred_name) {
      console.log('IDV-Modal: Enable preferred name');
      store.commit('identityVerification/setPreferredNameModal', true);
    } else if (!currentUser?.is_verified_email) {
      console.log('IDV-Modal: Enable email verification');
      store.commit('identityVerification/setIsCompanyEmailModal', true);
    }
  }
  // Allow brokers to onboard
  // if ((to.path.includes('/carrier/') || to.path == '/carrier') && !['carrier','broker'].includes(companyType)){
  if (
    (to.path.includes('/carrier/') || to.path == '/carrier') &&
    companyType != 'carrier'
  ) {
    console.log('/carrier namespace guard; redirecting');
    return next(homePage);
  }

  // if ((to.path.includes('/broker/') || to.path == '/broker') && companyType != 'broker'){
  //   console.log('/broker namespace guard; redirecting')
  //   return next(homePage)
  // }

  if (
    (to.path.includes('/dispatcher/') || to.path == '/dispatcher') &&
    companyType != 'dispatcher'
  ) {
    console.log('/dispatcher namespace guard; redirecting');
    return next(homePage);
  }

  if (to.path == '/onboarding' || to.path == '/') {
    console.log('redirect root to home page');
    return next(homePage);
  }

  console.log('all guards cleared; sending to', to);
  // you made it.  congrats bro.
  return next();
});

async function handleBrokerUser(currentUser, currentCompany, to, from, next) {
  let isActiveBrokerUserNotAssociatedWithCarrier =
    currentUser &&
    currentCompany &&
    currentCompany.company_type == 'broker' &&
    currentCompany.carrier_id == null;

  console.log('a broker user that is already logged in');
  // Handle a logged in broker user that is trying to claim a carrier
  if (
    isActiveBrokerUserNotAssociatedWithCarrier &&
    currentUser.requested_carrier_id &&
    !to.meta.allowBrokerUserToAccess
  ) {
    console.log(to);
    console.log(
      'a broker user that is already logged in and attempting to claim a carrier'
    );
    return next({ name: 'FMSCAAccountVerification' });
  }

  // Handle an existing broker user that has not associated themselves with a company
  // and needs to select a carrier
  if (
    isActiveBrokerUserNotAssociatedWithCarrier &&
    currentUser.requested_carrier_id == null &&
    to.name != 'CarrierDetails'
  ) {
    console.log(to);
    console.log('broker user that has not been associated with a carrier');
    return next({ name: 'CarrierDetails' });
  }

  console.log('all guards cleared; sending to', to);
  return next();
}

// Store the oauth params in local storage if they are present

async function storeOauthParams(to) {
  const { query, fullPath } = to;
  // 1. store every non-empty query param in localStorage
  Object.entries(query).forEach(([key, val]) => {
    if (val != null && val !== '') {
      console.log(`storing oauth ${key}`);
      localStorage.setItem(key, val);
    }
  });

  // 2. if we got a new client_id but no classification_ids, nuke the old ones
  if ('client_id' in query && !('classification_ids' in query)) {
    console.log('removing old oauth classification_ids');
    localStorage.removeItem('classification_ids');
  }

  // 3. capture full route on oauth/authorizations in sessionStorage
  if (fullPath.includes('/oauth/authorizations')) {
    sessionStorage.setItem(
      'redirect_oauth',
      JSON.stringify(JSON.stringify(to))
    );
  }
}

export default router;
